/**
 * Imperative optimistic submit helper for callers outside the React Query
 * mutation lifecycle (e.g. `onMutate`/`onError` hooks aren't available).
 *
 * `applyOptimisticBatch` runs exactly once per call — a single `ctx` is the
 * sole rollback handle, and it is only fired on terminal failure (after
 * retries are exhausted or a non-retryable error is thrown). Conflict
 * retries rebuild and resubmit the `EventBatch`; they never re-apply the
 * optimistic store write.
 *
 * On a 409 conflict, the retry rebuild MUST start from `lastConflict.lastSeq`
 * (falling back to the previous attempt's start seq, never `0`) — never by
 * re-reading the store's live sequence number. The one-time pre-apply read at
 * the top of this function is the ONLY place that reads the live store seq;
 * by the time a retry runs, the store already holds our own optimistic bump,
 * so reading it again would double-count and ship seq+2, producing a
 * non-recoverable 400 instead of a clean retry.
 *
 * `errorToast: false` suppresses only the toast — the returned promise
 * always rejects on terminal failure regardless of this option.
 *
 * `fallbackSeq` is a floor, maxed with the store's live seq. Its primary
 * role is covering callers whose freshest seq isn't tracked in the store at
 * all (e.g. the slideout's focused-object query).
 *
 * `sourceType` is hard-coded to `'ahoy_user'`, which is correct for the note
 * surfaces this helper was built for. A future system/automation caller
 * would need this to be a parameter.
 */

import { toast } from 'sonner';

import type {
  EventBatch,
  OverwriteTarget,
  SubmitEventsResponse,
} from '../../../types/crm';
import { MAX_CONFLICT_RETRIES } from '../core/constants';
import { createCRMObjectClient } from '../core/CRMObjectClient';
import {
  getUserFriendlyEventError,
  PropertyTypeConflictError,
} from '../core/errors';
import { createEventBuilder, type EventBuilder } from '../core/EventBuilder';
import type { EntityTypeName } from '../core/types';
import { getObjectSeq } from './crmObjectStore';
import {
  applyOptimisticBatch,
  applySingleCardinalityReplacements,
  rollbackOptimisticBatch,
  type SingleCardinalityReplacement,
} from './optimisticBatch';

export interface SubmitEventsOptimisticParams {
  orgId: number;
  /**
   * Actor id for the event `source_id`. Accepts the caller's id verbatim
   * (`string` or `number`) and is stringified once via `String(userId)` —
   * NEVER coerced through `Number(...)`, which would turn a non-numeric auth
   * id into the literal `"NaN"` source id. Callers pass their raw
   * `user.id` / `userId`; the string form is the wire value.
   */
  userId: string | number;
  entityTypeName: EntityTypeName;
  primaryEntityId: string;
  build: (builder: EventBuilder) => void;
  fallbackSeq?: number;
  maxConflictRetries?: number;
  errorToast?: false | { message: (error: unknown) => string };
  /**
   * Forwarded verbatim to `client.submitEvents` as `overwrite_targets` for
   * create/upsert callers that need dedup-target matching. Omitted from the
   * wire payload entirely when empty/undefined.
   */
  overwriteTargets?: OverwriteTarget[];
  /**
   * Many-to-one association swaps folded into this batch's optimistic
   * rollback context — see `applySingleCardinalityReplacements`. Applied
   * once, immediately after the first optimistic apply, so conflict retries
   * never re-apply the store write.
   */
  singleCardinalityReplacements?: SingleCardinalityReplacement[];
}

export async function submitEventsOptimistic(
  p: SubmitEventsOptimisticParams
): Promise<SubmitEventsResponse> {
  const maxRetries =
    p.maxConflictRetries != null && p.maxConflictRetries >= 0
      ? p.maxConflictRetries
      : MAX_CONFLICT_RETRIES;
  const startSeq0 = Math.max(
    getObjectSeq(p.primaryEntityId) ?? 0,
    p.fallbackSeq ?? 0
  );

  const b0 = createEventBuilder({
    sourceType: 'ahoy_user',
    sourceId: String(p.userId),
    startSeq: startSeq0,
  });
  p.build(b0);
  const firstBatch: EventBatch = {
    events: b0.build(),
    ...(p.overwriteTargets?.length
      ? { overwrite_targets: p.overwriteTargets }
      : {}),
  };

  if (firstBatch.events.length === 0) {
    throw new Error(
      'submitEventsOptimistic: build produced no events — the caller must short-circuit no-op mutations before calling this helper'
    );
  }

  const ctx = applyOptimisticBatch(firstBatch, p.orgId);
  if (p.singleCardinalityReplacements?.length) {
    applySingleCardinalityReplacements(ctx, p.singleCardinalityReplacements);
  }
  try {
    let prevStartSeq = startSeq0;
    let lastConflict: PropertyTypeConflictError | undefined;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const client = createCRMObjectClient(p.orgId, p.entityTypeName);
      let batch: EventBatch;
      if (attempt === 0) {
        batch = firstBatch;
      } else {
        const freshSeq = lastConflict?.lastSeq ?? prevStartSeq;
        prevStartSeq = freshSeq;
        const b = createEventBuilder({
          sourceType: 'ahoy_user',
          sourceId: String(p.userId),
          startSeq: freshSeq,
        });
        p.build(b);
        batch = {
          events: b.build(),
          ...(p.overwriteTargets?.length
            ? { overwrite_targets: p.overwriteTargets }
            : {}),
        };
      }
      try {
        return await client.submitEvents(batch);
      } catch (err) {
        if (err instanceof PropertyTypeConflictError && attempt < maxRetries) {
          lastConflict = err;
          continue;
        }
        throw err;
      }
    }
    throw new Error('submitEventsOptimistic: max conflict retries exceeded');
  } catch (error) {
    rollbackOptimisticBatch(ctx);
    if (p.errorToast !== false) {
      toast.error(
        p.errorToast?.message
          ? p.errorToast.message(error)
          : getUserFriendlyEventError(error)
      );
    }
    throw error;
  }
}
